import pinia from '@/store';
import useMenuStore from '@/store/menu';
import { createStorage, STORAGE_KEYS } from './storage';

type FocusableElement = HTMLElement & {
  focus?: () => void;
  click?: () => void;
  __vue__?: any;
  _vnode?: any;
  __vueParentComponent?: any;
};

interface FocusManagerOptions {
  selector?: string;
  focusClass?: string;
}

interface MousePos {
  x: number;
  y: number;
}

interface recordFocusAreaInfo {
  latestType: string;
  [key: string]: string;
}

type EventType = 'back' | 'left' | 'right' | 'up' | 'down';

type OnChangeCallback = (newVal: any) => void;

/**
 * LG WebOS 默认退出应用页面
 */
export const PLATFORM_BACK_PAGE_PATHS = ['/', '/login'];

class FocusManager {
  private selector: string;
  private focusClass: string;
  private current: FocusableElement | null;
  private customRoutes: Map<string, (direction: string) => FocusableElement | null>;
  private handlers: Map<EventType, (curEl: FocusableElement | null) => boolean | void>;
  private moveAreaLimit: Map<string, EventType[]>;
  private focusChangeListeners: Map<string, OnChangeCallback>;
  private mousePos: MousePos | null;
  private focusRecord4page: Map<string, recordFocusAreaInfo>;
  private lastMoveTime = 0;
  private lastHorizontalMoveTime = 0;
  private lastEnterTime = 0;
  private readonly VERTICAL_DEBOUNCE_DELAY = 0; // No debounce - instant response
  private readonly HORIZONTAL_DEBOUNCE_DELAY = 0; // No debounce - instant response
  private readonly ENTER_DEBOUNCE_DELAY = 250; // Prevent rapid enter activations
  // Task 1.3: Cache focusable elements per section/group for faster vertical navigation
  private sectionFocusablesCache: Map<number, FocusableElement[]> = new Map();
  private sectionCacheTimestamp: Map<number, number> = new Map();
  private readonly SECTION_CACHE_DURATION = 2000; // 2 seconds TTL for section caches

  constructor(options: FocusManagerOptions = {}) {
    this.selector = options.selector || '[data-focusable]';
    this.focusClass = options.focusClass || 'focused';
    this.current = null;
    this.customRoutes = new Map();
    this.handlers = new Map();
    this.moveAreaLimit = new Map();
    this.focusChangeListeners = new Map();
    this.mousePos = null;
    this.focusRecord4page = new Map();
  }

  setPagefocusRecord(pageName: string, recordFocusEl: FocusableElement) {
    if (!recordFocusEl.dataset.focusType || !recordFocusEl.dataset.focusIndex) return;

    this.focusRecord4page.set(pageName, {
      ...this.focusRecord4page.get(pageName),
      [recordFocusEl.dataset.focusType as string]: recordFocusEl.dataset.focusIndex,
      latestType: recordFocusEl.dataset.focusType,
    });
  }

  removePagefocusRecord(pageName: string) {
    this.focusRecord4page.delete(pageName);
  }

  clearPagefocusRecord() {
    this.focusRecord4page.clear();
  }

  hasPagefocusRecord(pageName: string) {
    return Boolean(this.focusRecord4page.get(pageName));
  }

  restoreRecordFocusEl(pageName: string, type?: string) {
    const recordType = type || (this.focusRecord4page.get(pageName)?.latestType as string);

    const recordElIndex = this.focusRecord4page.get(pageName)?.[recordType];

    let firstElInViewport: FocusableElement | null = null;
    let restoreEl: FocusableElement | null = null;

    const allCandidates = document.querySelectorAll<FocusableElement>(this.selector);

    for (const el of allCandidates) {
      if (el.offsetParent === null) continue;

      if (!firstElInViewport) {
        firstElInViewport = el;
      }

      if (recordElIndex && el.dataset.focusType === recordType && el.dataset.focusIndex === recordElIndex) {
        restoreEl = el;
        break;
      }
    }
    // Instant restore - no delay
    if (restoreEl) {
      this.setFocus(restoreEl);
    } else if (firstElInViewport) {
      this.setFocus(firstElInViewport);
    }
  }

  // 记录鼠标位置
  setMousePos(position: MousePos) {
    this.mousePos = position;
  }

  // 注册聚焦元素变化的通知
  registerFocusChangeListener(key: string, cb: OnChangeCallback) {
    this.focusChangeListeners.set(key, cb);
  }

  // 取消注册聚焦元素变化的通知
  offFocusChangeListener(key: string) {
    this.focusChangeListeners.delete(key);
  }

  // 清空注册聚焦元素变化的通知
  clearFocusChangeListener() {
    this.focusChangeListeners.clear();
  }

  init() {
    const first = document.querySelector<FocusableElement>(this.selector);
    if (first) this.setFocus(first);
  }

  setFocus(el: FocusableElement) {
    // CRITICAL: Check if el exists before accessing .matches (prevents crash)
    // This can happen if element is destroyed/unmounted before setFocus is called
    if (!el || typeof el.matches !== 'function') {
      console.warn('[FocusManager] setFocus called with invalid element:', el);
      return;
    }
    if (!el.matches(this.selector)) return;
    if (!document.contains(el)) return;
    if (this.current && !document.contains(this.current)) {
      this.current = null;
    }

    this.setFocusTV(el);
  }

  private setFocusTV(el: FocusableElement) {
    // Force a DOM reflow before focus() and retry once if the element didn't receive focus.
    // The reflow ensures the visual focus state updates immediately on all TV browsers
    // (LG WebOS and Hisense VIDAA require it; it is a harmless no-op on Tizen/Samsung
    // since their browser handles focus() synchronously). The retry guard never fires
    // on well-behaved browsers where document.activeElement is set after the first call.
    try {
      if (this.current) {
        this.current?.blur();
        this.current.classList.remove(this.focusClass);
      }

      this.current = el;
      this.current.classList.add(this.focusClass);

      void this.current.offsetHeight; // force reflow

      this.current.focus?.({ preventScroll: true });

      if (document.activeElement !== this.current) {
        this.current?.focus?.({ preventScroll: true });
      }

      if (this.current.dataset.focusType !== 'menu') {
        this.setPagefocusRecord(window.location.href, this.current);
      }

      this.focusChangeListeners.forEach(cb => cb(this.current));
    } catch (error) {
      console.warn('[FocusManager] TV focus failed, using fallback:', error);
      this.setFocusDefault(el);
    }
  }

  private setFocusDefault(el: FocusableElement) {
    // Default focus handling
    if (this.current) {
      this.current?.blur();
      this.current.classList.remove(this.focusClass);
    }
    this.current = el;

    if (this.current) {
      this.current.classList.add(this.focusClass);
      this.current.focus?.({ preventScroll: true });

      if (this.current.dataset.focusType !== 'menu') {
        this.setPagefocusRecord(window.location.href, this.current);
      }
    }

    this.focusChangeListeners.forEach(cb => cb(this.current));
  }

  clearFocus() {
    if (this.current) {
      this.current.blur();
      this.current.classList.remove(this.focusClass);
      this.current = null;
      this.focusChangeListeners.forEach(cb => cb(this.current));
    }
  }

  getCurFocusEl() {
    return this.current;
  }

  // 注册自定义导航规则
  registerRoute(identifier: string, handler: (direction: string) => FocusableElement | null) {
    this.customRoutes.set(identifier, handler);
  }
  removeRegisterRoute(identifier: string) {
    this.customRoutes.delete(identifier);
  }

  // 获取当前元素的导航标识符
  private getRouteIdentifier(): string | null {
    if (!this.current) return null;
    const type = this.current.dataset.focusType || 'default';
    const index = this.current.dataset.focusIndex || '';
    return `${type}:${index}`;
  }

  // 设置自定义事件处理
  setHandler(event: EventType, handler: (curEl: FocusableElement | null) => boolean | void) {
    this.handlers.set(event, handler);
  }

  getHandler(event: EventType) {
    return this.handlers.get(event);
  }

  // 移除自定义事件处理
  removeHandler(event: EventType) {
    this.handlers.delete(event);
  }

  // 清除自定义事件处理
  clearHandler() {
    this.handlers.clear();
  }

  // 设置移动区域限制
  setMoveAreaLimit(area: string, limitList: EventType[]) {
    this.moveAreaLimit.set(area, limitList);
  }

  // 移除移动区域限制
  removeMoveAreaLimit(area: string) {
    this.moveAreaLimit.delete(area);
  }

  // 清除移动区域限制
  clearMoveAreaLimit() {
    this.moveAreaLimit.clear();
  }

  private inViewport(el: FocusableElement) {
    const rect = el.getBoundingClientRect();

    // const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
      // rect.bottom >= 0 && // 元素底部在视口上方以下（部分进入）
      // rect.top <= viewportHeight && // 元素顶部在视口底部以上
      rect.right >= 0 && // 元素右侧在视口左边以右（兼容 RTL）
      rect.left <= viewportWidth // 元素左侧在视口右边以左
    );
  }

  private findParentWithClass(el: HTMLElement, targetClass: string) {
    let parent = el.parentElement;
    while (parent) {
      if (parent.classList.contains(targetClass)) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  // Cache for focusable elements
  private elementsCache: FocusableElement[] | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION = 100; // Cache for 100ms - short duration for virtualized components

  private getFocusableElements(): FocusableElement[] {
    const now = Date.now();

    // Use cached elements if available and not stale
    if (this.elementsCache && now - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.elementsCache;
    }

    // Query and cache elements
    this.elementsCache = Array.from(document.querySelectorAll<FocusableElement>(this.selector));
    this.cacheTimestamp = now;

    return this.elementsCache;
  }

  // Invalidate cache when DOM changes
  invalidateCache() {
    this.elementsCache = null;
    // Task 1.3: Also invalidate section caches
    this.sectionFocusablesCache.clear();
    this.sectionCacheTimestamp.clear();
  }

  // Task 1.3: Get focusable elements for a specific section/group (cached)
  private getFocusablesForSection(groupIndex: number): FocusableElement[] {
    const now = Date.now();
    const cached = this.sectionFocusablesCache.get(groupIndex);
    const timestamp = this.sectionCacheTimestamp.get(groupIndex);

    // Return cached if fresh
    if (cached && timestamp && now - timestamp < this.SECTION_CACHE_DURATION) {
      // Filter out elements no longer in DOM
      return cached.filter(el => document.contains(el));
    }

    // Query only elements in this section
    const selector = `[data-focus-type="matchCardGroup_${groupIndex}"]`;
    const elements = Array.from(document.querySelectorAll<FocusableElement>(selector));

    // Cache the results
    this.sectionFocusablesCache.set(groupIndex, elements);
    this.sectionCacheTimestamp.set(groupIndex, now);

    return elements;
  }

  // Task 1.3: Get section index from focusable element
  private getSectionIndex(el: FocusableElement | null): number | null {
    if (!el?.dataset?.focusIndex) return null;

    // Extract group index from focusIndex (format: "groupIndex_cardIndex")
    const parts = el.dataset.focusIndex.split('_');
    if (parts.length >= 2) {
      const groupIndex = parseInt(parts[0], 10);
      if (!isNaN(groupIndex)) return groupIndex;
    }

    return null;
  }

  // New: Find the nearest horizontal scroll/carousel container
  private findVirtualContainer(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
      // Ignore our virtual container, it has overflow hidden but shouldn't scroll
      if (parent.classList.contains('virtual-carousel')) {
        parent = parent.parentElement;
        continue;
      }
      // Check for common carousel/virtualization container classes (add your Vue component class here if known)
      if (
        parent.classList.contains('carousel-container') ||
        parent.classList.contains('virtual-row') ||
        parent.dataset.isVirtual
      ) {
        return parent;
      }
      const style = getComputedStyle(parent);
      // Check for horizontal scroll container
      if (style.overflowX === 'scroll' || style.overflowX === 'auto') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  // New: Handle the virtualization move by scrolling the container and waiting for DOM update
  private async handleVirtualizationMove(direction: 'left' | 'right', currentEl: FocusableElement): Promise<boolean> {
    // First check if this element is in our custom virtual carousel
    // We need to check directly since findVirtualContainer skips virtual-carousel
    let parent = currentEl.parentElement;
    while (parent) {
      if (parent.classList.contains('virtual-carousel')) {
        // If it's our virtual carousel, DO NOT SCROLL NATIVELY.
        // The Vue component (Home.vue) listens to focus changes and updates the transform.
        // Returning false allows normal focus navigation to proceed.
        return false;
      }
      parent = parent.parentElement;
    }

    // Detect if this is in another virtualized container
    const container = this.findVirtualContainer(currentEl);

    if (!container) {
      return false; // Not in a virtualized container
    }

    // Check if the current element is near the edge of the *visible* container.
    const containerRect = container.getBoundingClientRect();
    const currentRect = currentEl.getBoundingClientRect();

    // Check if the current element is close to the visible edge of the container
    const isAtLeftEdge = currentRect.left <= containerRect.left + currentRect.width / 4;
    const isAtRightEdge = currentRect.right >= containerRect.right - currentRect.width / 4;

    let shouldScroll = false;
    if (direction === 'right' && isAtRightEdge) {
      shouldScroll = true;
    } else if (direction === 'left' && isAtLeftEdge) {
      shouldScroll = true;
    }

    if (shouldScroll) {
      console.log('🎠 [VIRTUAL] Edge reached. Triggering scroll to shift window...');

      // Use a scroll amount slightly larger than the card width to ensure the next card is rendered
      const scrollAmount = currentEl.offsetWidth + 10;

      container.scrollTo({
        left: container.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount),
        behavior: 'auto', // Instant scroll is preferred for virtualization
      });

      // **Crucial Step: Wait for the DOM to update**
      await new Promise(resolve => setTimeout(resolve, 50)); // Wait 50ms for Vue/Virtualization to re-render

      // Invalidate the cache to ensure the next `getFocusableElements()` gets the new DOM
      this.invalidateCache();

      console.log('🎠 [VIRTUAL] Scroll triggered and waited. Retrying navigation.');
      return true; // Indicate that a virtualization move was handled
    }

    return false; // No scroll needed
  }

  // Enhanced vertical scroll handler for webOS 5.0+ compatibility
  private handleVerticalScroll(targetEl: FocusableElement) {
    let scrollBlock = 'center' as 'center' | 'start' | 'end' | undefined;
    let elementToScroll = targetEl;

    if (targetEl.dataset.focusType === 'banner') {
      scrollBlock = 'end';
    }
    // For all match cards, scroll to their parent section container
    // This ensures the entire section including title is visible
    else if (targetEl.dataset.focusType?.startsWith('matchCardGroup_')) {
      console.log('📦 [SCROLL] Match card detected, finding parent container');
      // Check if this is a widthBanner section
      if (targetEl.classList.contains('widthBannerCard')) {
        elementToScroll = this.findParentWithClass(targetEl, 'widthBanner') as FocusableElement;
        console.log('📦 [SCROLL] widthBanner found:', elementToScroll);
      } else {
        // For regular sections, find the matchGroup container
        elementToScroll = this.findParentWithClass(targetEl, 'matchGroup') as FocusableElement;
        console.log('📦 [SCROLL] matchGroup found:', elementToScroll);
      }
      scrollBlock = 'start'; // Align section to top of viewport
      console.log('📦 [SCROLL] Will scroll to section with block:', scrollBlock);
    }

    try {
      // First try modern scrollIntoView
      if (elementToScroll.scrollIntoView) {
        elementToScroll.scrollIntoView({
          behavior: 'auto', // Always use instant scroll for webOS 5.0+ compatibility
          block: scrollBlock,
        });
      } else {
        // Fallback for older webOS versions
        this.fallbackScroll(elementToScroll);
      }
    } catch (error) {
      console.warn('ScrollIntoView failed, using fallback:', error);
      this.fallbackScroll(elementToScroll);
    }
  }

  // Enhanced horizontal scroll handler for carousel and banner navigation
  private handleHorizontalScroll(targetEl: FocusableElement, direction: 'left' | 'right') {
    // Find the scrollable container for horizontal navigation
    const scrollContainer = this.findScrollContainer(targetEl);

    if (scrollContainer) {
      const targetRect = targetEl.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate if element is outside the visible area
      const isOutsideLeft = targetRect.left < containerRect.left;
      const isOutsideRight = targetRect.right > containerRect.right;

      if (isOutsideLeft || isOutsideRight) {
        try {
          targetEl.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: direction === 'left' ? 'end' : 'start',
          });
        } catch (error) {
          console.warn('Horizontal scroll failed, using fallback:', error);
          this.fallbackHorizontalScroll(scrollContainer, targetEl, direction);
        }
      }
    }
  }

  // Find the nearest scrollable container
  private findScrollContainer(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
      // Ignore our virtual container, it has overflow hidden but shouldn't scroll
      if (parent.classList.contains('virtual-carousel')) {
        parent = parent.parentElement;
        continue;
      }
      const style = getComputedStyle(parent);
      if (
        style.overflowX === 'scroll' ||
        style.overflowX === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflow === 'auto'
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.documentElement; // Fallback to document element
  }

  // Fallback scroll implementation for older browsers
  private fallbackScroll(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // If element is not in viewport, scroll it into view
    if (rect.top < 0 || rect.bottom > viewportHeight) {
      const scrollTop = window.pageYOffset + rect.top - viewportHeight / 2;
      window.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'auto', // Instant for compatibility
      });
    }
  }

  // Fallback horizontal scroll for older browsers
  private fallbackHorizontalScroll(container: HTMLElement, target: HTMLElement, direction: 'left' | 'right') {
    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (direction === 'left' && targetRect.left < containerRect.left) {
      const scrollLeft = container.scrollLeft - (containerRect.left - targetRect.left) - 50;
      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'auto',
      });
    } else if (direction === 'right' && targetRect.right > containerRect.right) {
      const scrollLeft = container.scrollLeft + (targetRect.right - containerRect.right) + 50;
      container.scrollTo({
        left: scrollLeft,
        behavior: 'auto',
      });
    }
  }

  // ⭐ The move method is now async to support waiting for virtualization updates
  async move(direction: 'left' | 'right' | 'up' | 'down') {
    // Debug logging to diagnose physical remote issues
    console.log(
      '🔵 [FOCUS] move() called, direction:',
      direction,
      'current:',
      this.current?.dataset?.focusIndex,
      'time:',
      Date.now(),
    );

    // For virtualized components: Always invalidate cache before navigation
    // This ensures we query fresh DOM after any reactive updates
    this.invalidateCache();

    // Check if menu is open first - menu navigation should NOT be debounced
    const menuStore = useMenuStore(pinia);
    const isMenuNavigation = menuStore.isExpand && (direction === 'up' || direction === 'down');

    // Direction-specific input debouncing for smooth TV navigation (skip for menu)
    if (!isMenuNavigation) {
      const now = Date.now();
      const isHorizontal = direction === 'left' || direction === 'right';

      if (isHorizontal) {
        if (now - this.lastHorizontalMoveTime < this.HORIZONTAL_DEBOUNCE_DELAY) {
          return; // Skip rapid horizontal inputs
        }
        this.lastHorizontalMoveTime = now;
      } else {
        if (now - this.lastMoveTime < this.VERTICAL_DEBOUNCE_DELAY) {
          return; // Skip rapid vertical inputs
        }
        this.lastMoveTime = now;
      }
    }

    // Check if menu is open - if so, restrict navigation to menu items only
    if (menuStore.isExpand) {
      // For vertical navigation when menu is open, only allow menu item navigation
      if (direction === 'up' || direction === 'down') {
        // Use cached elements to find menu items for better performance
        const menuItems = this.getFocusableElements().filter(el => el.dataset.focusType === 'menu');
        if (menuItems.length === 0) return;

        const currentIndex = menuItems.indexOf(this.current as FocusableElement);
        let nextIndex = currentIndex;

        if (direction === 'up') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
        } else if (direction === 'down') {
          nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
        }

        console.log('🔥 [MENU NAV] Next index:', nextIndex);
        console.log('🔥 [MENU NAV] Next element:', menuItems[nextIndex]);

        if (menuItems[nextIndex]) {
          console.log('🔥 [MENU NAV] Setting focus to menu item:', nextIndex);
          this.setFocus(menuItems[nextIndex]);
          console.log('🔥 [MENU NAV] Focus set, current element now:', this.current);
        }
        return;
      }
      // For horizontal navigation, handle menu closing on right arrow (for English)
      const locale = createStorage(STORAGE_KEYS.LOCALE, 'ar') as 'en' | 'ar';
      if ((locale === 'en' && direction === 'right') || (locale === 'ar' && direction === 'left')) {
        menuStore.setMenuExpand(false);
        // Just close the menu, don't navigate - user can press right again if they want to move
        return;
      }
      // Block left navigation when menu is open
      if ((locale === 'en' && direction === 'left') || (locale === 'ar' && direction === 'right')) {
        return; // Don't allow moving further left when menu is open
      }
    }

    // 自定义导航规则，适配别扭的移动聚焦
    const routeKey = this.getRouteIdentifier();
    if (routeKey && this.customRoutes.has(routeKey)) {
      const handler = this.customRoutes.get(routeKey)!;
      const target = handler(direction);
      // Check if target is valid before calling setFocus (prevents crash)
      // Target could be undefined/null if element was destroyed
      if (target && typeof target === 'object' && 'matches' in target) {
        this.setFocus(target);
        return;
      }
    }

    let centerX = 0;
    let centerY = 0;
    let currentRect: any = null; // Changed to 'any' for initialization flexibility

    // Safety check: if current element is not in DOM, clear it
    if (this.current && !document.contains(this.current)) {
      console.log('⚠️ [FOCUS] Current element not in DOM, clearing reference');
      this.current = null;
    }

    if (this.current) {
      const locale = createStorage(STORAGE_KEYS.LOCALE, 'ar') as 'en' | 'ar';

      if (
        this.current?.dataset.focusType === 'menu' &&
        ((locale === 'en' && direction === 'right') || (locale === 'ar' && direction === 'left'))
      ) {
        const menuStore = useMenuStore(pinia);
        menuStore.setMenuExpand(false);
        return;
      }

      currentRect = this.current.getBoundingClientRect();
      centerX = currentRect.left + currentRect.width / 2;
      centerY = currentRect.top + currentRect.height / 2;
    } else {
      currentRect = {
        width: 20,
        height: 20,
        // Provide defaults for safe use later
      };
      centerX = this.mousePos?.x || 0;
      centerY = this.mousePos?.y || 0;
    }

    // Ensure currentRect is initialized before use in filter
    if (!currentRect) {
      currentRect = { width: 20, height: 20 };
    }

    // Task 1.3: Optimize vertical navigation - use section cache when moving between rows
    let all: FocusableElement[] = [];
    const currentSectionIndex = this.getSectionIndex(this.current);

    if ((direction === 'up' || direction === 'down') && currentSectionIndex !== null) {
      // For vertical navigation, try section-specific cache first
      const targetSectionIndex = direction === 'down' ? currentSectionIndex + 1 : currentSectionIndex - 1;

      if (targetSectionIndex >= 0) {
        const sectionElements = this.getFocusablesForSection(targetSectionIndex);
        if (sectionElements.length > 0) {
          // Check if any element in target section is already visible (fast path)
          const visibleInTarget = sectionElements.find(el => {
            if (!document.contains(el)) return false;
            const rect = el.getBoundingClientRect();
            const elCenterX = rect.left + rect.width / 2;
            const elCenterY = rect.top + rect.height / 2;
            const dx = elCenterX - centerX;
            const dy = elCenterY - centerY;

            if (direction === 'down') {
              return dy > 0 && Math.abs(dx) < currentRect.width && this.inViewport(el);
            } else {
              return dy < 0 && Math.abs(dx) < currentRect.width && this.inViewport(el);
            }
          });

          if (visibleInTarget) {
            // Fast path: element already visible, use section cache only
            all = sectionElements;
          } else {
            // Still need to check all elements, but prioritize section cache
            const allElements = this.getFocusableElements();
            all = [...sectionElements, ...allElements.filter(el => !sectionElements.includes(el))];
          }
        } else {
          // Fallback to full query if section cache miss
          all = this.getFocusableElements();
        }
      } else {
        // Moving to banner or outside sections, use full query
        all = this.getFocusableElements();
      }
    } else {
      // Horizontal navigation or no section context, use full cache
      all = this.getFocusableElements();
    }

    // Filter the elements
    all = all.filter(
      // Filter for area restrictions and ensure elements are in DOM
      el => {
        // For virtualized components: Only exclude if element is not in DOM
        // offsetParent can be null temporarily during re-renders
        if (!document.contains(el)) {
          return false; // Exclude elements not in DOM
        }

        // Apply move area limits if set
        if (
          this.current?.dataset.focusType &&
          this.moveAreaLimit.has(this.current?.dataset.focusType) &&
          this.moveAreaLimit.get(this.current?.dataset?.focusType)?.includes(direction)
        ) {
          return el.dataset.focusType === this.current?.dataset.focusType;
        } else {
          return true; // Accept all focusable elements that are in DOM
        }
      },
    );

    console.log('🟢 [FOCUS] All focusable elements found:', all.length);

    // For virtualized carousels: log all matchCardGroup elements
    const carouselCards = all.filter(el => el.dataset.focusType?.includes('matchCardGroup'));
    if (carouselCards.length > 0) {
      console.log('🎠 [FOCUS] Carousel cards in DOM:', carouselCards.map(el => el.dataset.focusIndex).join(', '));
    }

    const candidates = all
      .filter(el => el !== this.current)
      .map(el => {
        const rect = el.getBoundingClientRect();
        const elCenterX = rect.left + rect.width / 2;
        const elCenterY = rect.top + rect.height / 2;

        return {
          el,
          rect,
          centerX: elCenterX,
          centerY: elCenterY,
          dx: elCenterX - centerX,
          dy: elCenterY - centerY,
        };
      })
      .filter(({ el, dx, dy }) => {
        // const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        const focusCheckHeight = (el?.dataset?.focusCheckHeight as any) || 3;
        switch (direction) {
          case 'left':
          case 'right': {
            const isMovingLeft = direction === 'left';
            const isHorizontal = isMovingLeft ? dx < 0 : dx > 0;
            const withinVerticalRange =
              Math.abs(dy) < currentRect.height * focusCheckHeight || this.current?.dataset.focusType === 'banner';
            const sameType = el.dataset.focusType === this.current?.dataset.focusType;

            // Debug logging for carousel cards
            if (el.dataset.focusType?.includes('matchCardGroup')) {
              console.log(
                `🎯 [FOCUS] Checking card ${el.dataset.focusIndex}: dx=${dx.toFixed(0)}, dy=${dy.toFixed(0)}, isHorizontal=${isHorizontal}, withinVert=${withinVerticalRange}, sameType=${sameType}`,
              );
            }

            // Prefer same type, but allow cross-type if banner or within tight vertical range (< 1x height)
            const allowCrossType = this.current?.dataset.focusType === 'banner' || Math.abs(dy) < currentRect.height;

            return isHorizontal && withinVerticalRange && (sameType || allowCrossType);
          }
          case 'up': {
            // Task 1.3: For vertical moves, check if element is already visible first (fast path)
            const isAlreadyVisibleUp = dy < 0 && Math.abs(dx) < currentRect.width && this.inViewport(el);
            if (isAlreadyVisibleUp) return true;

            return (
              (dy < 0 && Math.abs(dx) < currentRect.width) ||
              (dy < 0 &&
                el.dataset.focusType !== this.current?.dataset.focusType &&
                Math.abs(dx) < window.innerWidth &&
                this.inViewport(el))
            );
          }
          case 'down': {
            // Task 1.3: For vertical moves, check if element is already visible first (fast path)
            const isAlreadyVisibleDown = dy > 0 && Math.abs(dx) < currentRect.width && this.inViewport(el);
            if (isAlreadyVisibleDown) return true;

            return (
              (dy > 0 && Math.abs(dx) < currentRect.width) ||
              (dy > 0 &&
                el.dataset.focusType !== this.current?.dataset.focusType &&
                Math.abs(dx) < window.innerWidth &&
                el.dataset.focusType !== 'banner')
            );
          }
          default:
            return false;
        }
      });

    console.log('🟡 [FOCUS] Candidates after direction filter:', candidates.length);

    // 根据主轴距离排序
    candidates.sort((a, b) => {
      // CRITICAL: Prioritize same-type elements (same carousel) over cross-type
      // This ensures horizontal navigation stays within the current carousel
      const currentType = this.current?.dataset?.focusType;
      const aIsSameType = a.el.dataset?.focusType === currentType;
      const bIsSameType = b.el.dataset?.focusType === currentType;

      // If one is same type and the other isn't, prefer the same type
      if (aIsSameType && !bIsSameType) return -1;
      if (!aIsSameType && bIsSameType) return 1;

      // If both are same type or both are different type, sort by distance
      const primaryA = direction === 'left' || direction === 'right' ? Math.abs(a.dx) : Math.abs(a.dy);
      const primaryB = direction === 'left' || direction === 'right' ? Math.abs(b.dx) : Math.abs(b.dy);
      const secondaryA = direction === 'left' || direction === 'right' ? Math.abs(a.dy) : Math.abs(a.dx);
      const secondaryB = direction === 'left' || direction === 'right' ? Math.abs(b.dy) : Math.abs(b.dx);

      if (primaryA !== primaryB) return primaryA - primaryB;
      return secondaryA - secondaryB;
    });

    if (candidates.length > 0) {
      console.log(
        '🟣 [FOCUS] Best candidate:',
        candidates[0].el.dataset.focusIndex,
        'type:',
        candidates[0].el.dataset.focusType,
      );
    }

    // 自定义移动响应事件
    let flag = true;
    const moveHandler = this.handlers.get(direction);
    if (moveHandler) {
      flag = moveHandler?.(candidates[0]?.el) ?? false;
      console.log('🟠 [FOCUS] Custom handler returned:', flag);
    }
    if (!flag) {
      console.log('🔴 [FOCUS] Custom handler blocked navigation');
      return;
    }

    // ⭐ START: New Virtualization/Recycling Handling
    if (candidates.length === 0 && ['left', 'right'].includes(direction) && this.current) {
      console.log('🔴 [FOCUS] No candidates found - Checking virtualization needs.');

      const isVirtualMoveAttempted = await this.handleVirtualizationMove(direction as 'left' | 'right', this.current);

      if (isVirtualMoveAttempted) {
        console.log('🔄 [FOCUS] Retrying move after virtualization shift.');
        // Recursive call to retry navigation with the updated DOM
        this.move(direction);
        return;
      }

      console.log('🔴 [FOCUS] No candidates found - navigation blocked');
      return;
    }
    // ⭐ END: New Virtualization/Recycling Handling

    if (candidates.length === 0) {
      console.log('🔴 [FOCUS] No candidates found - navigation blocked');
      return;
    }

    // Enhanced scroll navigation for webOS 5.0+ compatibility
    if (
      ['up', 'down'].includes(direction) &&
      candidates[0].el.dataset.focusType !== this.current?.dataset.focusType &&
      document.querySelector('.home_container')
    ) {
      this.handleVerticalScroll(candidates[0].el);
    }

    // Handle horizontal scroll for banner items and carousel navigation
    if (
      ['left', 'right'].includes(direction) &&
      this.current?.dataset.focusType === candidates[0].el.dataset.focusType &&
      (this.current?.dataset.focusType === 'banner' || this.current?.dataset.focusType === 'card')
    ) {
      this.handleHorizontalScroll(candidates[0].el, direction as 'left' | 'right');
    }

    if (
      candidates[0].el.dataset.focusType !== this.current?.dataset.focusType &&
      this.focusRecord4page.get(window.location.href)?.[candidates[0].el.dataset.focusType as string]
    ) {
      console.log('✅ [FOCUS] Restoring recorded focus');
      this.restoreRecordFocusEl(window.location.href, candidates[0].el.dataset.focusType as string);
    } else {
      console.log('✅ [FOCUS] Setting focus to candidate');
      this.setFocus(candidates[0].el);
    }
    console.log('✅ [FOCUS] Navigation completed, new focus:', this.current?.dataset?.focusIndex);
  }

  enter() {
    // Never activate an element that is no longer in the document (e.g. after route change).
    if (this.current && !document.contains(this.current)) {
      this.current = null;
      return;
    }
    if (!this.current) return;

    // Debounce enter actions to prevent rapid repeated activations
    const now = Date.now();
    if (now - this.lastEnterTime < this.ENTER_DEBOUNCE_DELAY) {
      console.log('Enter action debounced - too soon after last activation');
      return;
    }
    this.lastEnterTime = now;

    console.log('FocusManager enter() triggered on element:', this.current);
    console.log('Element dataset:', this.current.dataset);

    // Check if element is visible and interactable
    const style = window.getComputedStyle(this.current);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
      console.log('Element is not visible, aborting enter()');
      return;
    }

    // Enhanced debugging and activation for different element types
    let activated = false;

    // Method 1: Try direct click first
    try {
      console.log('Trying direct click()...');
      this.current.click?.();
      activated = true;
      console.log('Direct click() succeeded');
    } catch (error) {
      console.warn('Direct click() failed:', error);
    }

    // Method 2: Enhanced MouseEvent dispatch - simplified to avoid multiple events
    if (!activated) {
      try {
        console.log('Dispatching enhanced MouseEvent...');

        const rect = this.current.getBoundingClientRect();
        const clientX = rect.left + rect.width / 2;
        const clientY = rect.top + rect.height / 2;

        const clickEvt = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 1,
          clientX,
          clientY,
          screenX: clientX,
          screenY: clientY,
          ctrlKey: false,
          altKey: false,
          shiftKey: false,
          metaKey: false,
          button: 0,
          buttons: 1,
          relatedTarget: null,
        });

        this.current.dispatchEvent(clickEvt);
        activated = true;
        console.log('Enhanced MouseEvent dispatch completed');
      } catch (error) {
        console.warn('Enhanced MouseEvent dispatch failed:', error);
      }
    }

    // Method 3: Direct keydown event for elements with @keydown.enter handlers
    try {
      console.log('Trying keydown Enter event...');
      const keyEvt = new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        keyCode: 13,
        which: 13,
        bubbles: false, // do not bubble – prevents document listener from calling enter() again
        cancelable: true,
      });
      this.current.dispatchEvent(keyEvt);
      console.log('Keydown Enter dispatched');
    } catch (error) {
      console.warn('Keydown Enter failed:', error);
    }

    // Method 4: Vue-specific activation for components
    try {
      console.log('Checking for Vue component activation...');

      // Check if this element or its parent has Vue component instance
      let vueEl = this.current;
      let vueInstance = null;

      // Look for Vue instance on element or parent elements
      for (let i = 0; i < 5 && vueEl; i++) {
        if (vueEl.__vue__ || vueEl._vnode || vueEl.__vueParentComponent) {
          vueInstance = vueEl.__vue__ || vueEl._vnode?.component || vueEl.__vueParentComponent;
          console.log('Found Vue instance at level', i, vueInstance);
          break;
        }
        vueEl = vueEl.parentElement as FocusableElement;
      }

      // Try to trigger Vue component events
      if (vueInstance && vueInstance.$emit) {
        console.log('Triggering Vue $emit click event');
        vueInstance.$emit('click');
      }

      console.log('Vue component activation attempted');
    } catch (error) {
      console.warn('Vue component activation failed:', error);
    }

    console.log('FocusManager enter() sequence completed');
  }

  back() {
    let flag = true;
    const backHandler = this.handlers.get('back');
    if (backHandler) {
      flag = backHandler?.(this.current) ?? false;
    }

    if (flag) {
      if (PLATFORM_BACK_PAGE_PATHS.includes(location.pathname)) {
        if (window.webOS) {
          window.webOS.platformBack?.();
        } else if (window.tizen) {
          window.tizen?.application?.getCurrentApplication?.().exit?.();
        }
      } else {
        history.back();
      }
    }
  }

  play() {
    console.log('Play triggered');
  }

  moveLeft() {
    this.move('left');
  }

  moveRight() {
    this.move('right');
  }

  moveUp() {
    this.move('up');
  }

  moveDown() {
    this.move('down');
  }
}

export default new FocusManager({
  selector: '[data-focusable]',
  focusClass: 'focused',
});
